import React from "react";

import { Text } from "@performyard/py-components";
import { MultipleChoiceOptionJson, RatingScaleOptionJson, SurveyQuestionJson } from "@performyard/service-contracts";

import InputTextArea from "../../../../../../../components/Inputs/InputTextArea";
import { Checkboxes } from "../../../../../../../components/Inputs/SelectionOptions/Checkboxes";
import { RadioButtons } from "../../../../../../../components/Inputs/SelectionOptions/RadioButtons";
import RatingScaleInput from "../../../../../../../components/Inputs/SelectionOptions/RatingScaleInput";
import Spacer from "../../../../../../../components/Spacer";

import { useAnswers, useErrors, useSetters } from "../store/store";

interface QuestionProps {
	question: SurveyQuestionJson;
}

export const InstructionsQuestion = ({ question }: QuestionProps) => {
	return (
		<div id={`question-${question._id}`} key={question._id}>
			<Text size="paragraph-1" weight="semi-bold" color="primary-5">
				{question.text}
			</Text>
			{question.subtitle && (
				<>
					<Spacer height={1} />
					<Text size="paragraph-3" weight="regular" color="neutral-7">
						<div className="is-markdown whitespace-pre-wrap">{question.subtitle}</div>
					</Text>
				</>
			)}
		</div>
	);
};

export const TextQuestion = ({ question }: QuestionProps) => {
	const setters = useSetters();
	const answers = useAnswers();

	const errors = useErrors();

	return (
		<div id={`question-${question._id}`} key={question._id}>
			<InputTextArea
				label={question.text}
				labelSize="paragraph-2"
				labelWeight="semi-bold"
				onChange={(value) => {
					if (errors[question._id]) {
						const newErrors = { ...errors };
						delete newErrors[question._id];
						setters.setErrors(newErrors);
					}
					setters.setAnswer(question._id, value);
				}}
				minRows={7}
				maxRows={7}
				value={(answers[question._id] as string) || ""}
				placeholder="Your answer here"
				errorMessage={errors[question._id]}
				subtitle={
					question.subtitle && (
						<>
							<Text size="paragraph-3" weight="regular" color="neutral-7">
								<div className="is-markdown whitespace-pre-wrap">{question.subtitle}</div>
							</Text>
							<Spacer height={1} />
						</>
					)
				}
			/>
		</div>
	);
};

export const MultipleChoiceQuestion = ({ question }: QuestionProps) => {
	const setters = useSetters();
	const answers = useAnswers();
	const errors = useErrors();

	const objectOptions = question?.options?.filter(
		(option): option is MultipleChoiceOptionJson => typeof option === "object"
	);
	const options =
		objectOptions
			?.filter((option) => !option.deleted)
			?.sort((a, b) => a.order - b.order)
			.map((option: MultipleChoiceOptionJson) => ({
				value: option._id,
				label: option.text,
			})) || [];

	return (
		<div
			className={
				// if the label is longer than 100 characters and contains any words longer than 100 characters, "break-all" forces the labels to wrap mid word
				options?.some(
					(value) => value.label.length > 100 && value.label.split(" ").some((word) => word.length > 100)
				)
					? "break-all"
					: ""
			}
			id={`question-${question._id}`}
			key={question._id}
		>
			<RadioButtons
				label={question.text}
				labelSize="paragraph-2"
				labelWeight="semi-bold"
				options={options}
				onChange={(value) => {
					if (errors[question._id]) {
						const newErrors = { ...errors };
						delete newErrors[question._id];
						setters.setErrors(newErrors);
					}
					setters.setAnswer(question._id, value.value);
				}}
				subtitle={
					question.subtitle && (
						<>
							<Text size="paragraph-3" weight="regular" color="neutral-7">
								<div className="is-markdown whitespace-pre-wrap">{question.subtitle}</div>
							</Text>
							<Spacer height={1} />
						</>
					)
				}
				value={{
					value: answers[question._id] as string,
					label: answers[question._id] as string,
				}}
				allowUnselect={true}
			/>
		</div>
	);
};

export const MultipleSelectQuestion = ({ question }: QuestionProps) => {
	const setters = useSetters();
	const answers = useAnswers();
	const errors = useErrors();

	const objectOptions = question?.options?.filter(
		(option): option is MultipleChoiceOptionJson => typeof option === "object"
	);
	const options =
		objectOptions
			?.filter((option) => !option.deleted)
			.sort((a, b) => a.order - b.order)
			.map((option: MultipleChoiceOptionJson) => ({
				value: option._id,
				label: option.text,
			})) || [];

	return (
		<div
			className={
				options.some(
					(value) => value.label.length > 100 && value.label.split(" ").some((word) => word.length > 100)
				)
					? "break-all"
					: ""
			}
			id={`question-${question._id}`}
			key={question._id}
		>
			<Checkboxes
				label={question.text}
				labelSize="paragraph-2"
				labelWeight="semi-bold"
				options={options}
				onChange={(value) => {
					if (errors[question._id]) {
						const newErrors = { ...errors };
						delete newErrors[question._id];
						setters.setErrors(newErrors);
					}
					const arrayValue = Array.isArray(value) ? value.map((option) => option.value) : [];
					setters.setAnswer(question._id, arrayValue);
				}}
				subtitle={
					question.subtitle && (
						<>
							<Text size="paragraph-3" weight="regular" color="neutral-7">
								<div className="is-markdown whitespace-pre-wrap">{question.subtitle}</div>
							</Text>
							<Spacer height={1} />
						</>
					)
				}
				value={
					Array.isArray(answers[question._id])
						? (answers[question._id] as string[]).map((ans) => ({
								value: ans,
								label: ans,
							}))
						: []
				}
			/>
		</div>
	);
};

export const RatingScaleQuestion = ({ question }: QuestionProps) => {
	const setters = useSetters();
	const answers = useAnswers();
	const errors = useErrors();

	const objectOptions = question?.options?.filter(
		(option): option is RatingScaleOptionJson => typeof option === "object"
	);

	const options =
		objectOptions
			?.filter((option) => !option.deleted)
			.sort((a, b) => a.order - b.order)
			.map((option: RatingScaleOptionJson) => ({
				value: option._id,
				text: option.text,
				description: option.description,
			})) || [];

	return (
		<div id={`question-${question._id}`} key={question._id}>
			<RatingScaleInput
				label={question.text}
				labelSize="paragraph-2"
				labelWeight="semi-bold"
				subtitle={
					question.subtitle && (
						<>
							<Text size="paragraph-3" weight="regular" color="neutral-7">
								<div className="is-markdown whitespace-pre-wrap">{question.subtitle}</div>
							</Text>
							<Spacer height={1} />
						</>
					)
				}
				options={options}
				onChange={(value) => {
					setters.setAnswer(question._id, value ? value.toString() : null);
				}}
				value={answers[question._id] as string}
				errorMessage={errors[question._id]}
			/>
		</div>
	);
};
